import { Dayjs } from 'dayjs';
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  CellControllerKind,
  InputDateControllerEntity,
} from 'entities/TableControllers';
import { dayJsToServerFormat } from 'utils/date';
import { debounce } from 'utils/debounce';

import { ITableControllerBase, TableControllerParams } from '../interface';

type PrivateFields = '_value';

const DATE_SELECT_DEBOUNCE_TIME = 900;

export default class TableDateController
  implements
    ITableControllerBase<InputDateControllerEntity['controller']['value']>
{
  constructor({
    record,
    onSave,
  }: TableControllerParams<InputDateControllerEntity>) {
    this._value = record.controller;
    this._initialState = record.controller;

    makeObservable<TableDateController, PrivateFields>(this, {
      _value: observable.ref,
      controllerValue: computed,

      toServerFormat: computed,

      destroy: action.bound,
    });

    const onSaveHandler = debounce(async () => {
      await onSave();
      this._isUpdateBlocked = false;
    }, DATE_SELECT_DEBOUNCE_TIME);

    this._reactions.push(
      reaction(
        () => this._value,
        async () => {
          this._isUpdateBlocked = true;
          await onSaveHandler();
        }
      )
    );
  }

  private readonly _reactions: IReactionDisposer[] = [];

  private _value: InputDateControllerEntity['controller'];
  private readonly _initialState: InputDateControllerEntity['controller'];

  private _isUpdateBlocked = false;

  get isUpdateBlocked(): boolean {
    return this._isUpdateBlocked;
  }

  get controllerValue() {
    return this._value.value;
  }

  get controllerKind() {
    return CellControllerKind.INPUT_DATE;
  }

  get toServerFormat() {
    return this._value.value ? dayJsToServerFormat(this._value.value) : null;
  }

  reset(): void {
    this._value = {
      value: null,
    };
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
  }

  setValue(value: Dayjs | null): void {
    // Handle null values
    if (value === null && this._value.value === null) {
      return;
    }

    if (value !== null && this._value.value !== null && value.isSame(this._value.value)) {
      return;
    }

    this._value = {
      value,
    };
  }
}
